import React, { useState, useEffect } from "react";
import {
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Share2,
  Target,
  BookOpen,
  Loader,
  AlertTriangle,
  TrendingUp,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  addDoc,
  setDoc,
  getDocs,
  limit,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import Tooltip from "../../components/Tooltip/JobSeekerTooltip"; // <-- Add this import
import UpgradeModal from "../../components/UpgradeModal";
import PremiumPaywall from "../../components/PremiumPaywall";
import toast from "react-hot-toast";

const TestResults = () => {
  const { user } = useAuth();
  const { isPro, loading: subscriptionLoading } = useSubscription();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const db = getFirestore(firebaseApp);

  const [selectedTest, setSelectedTest] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingCourse, setGeneratingCourse] = useState(false);
  const [courseGenerationStatus, setCourseGenerationStatus] = useState("");
  const [courseId, setCourseId] = useState(null);
  const [mistakesData, setMistakesData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(
    searchParams.get("showVerificationPrompt") === "true"
  );

  // Helper functions to generate meaningful data from test results
  const generateSections = (resultData) => {
    const score = resultData.score || 0;
    const totalQuestions = resultData.totalQuestions || 20;
    const correctAnswers = resultData.correctAnswers || 0;

    // Generate sections based on question categories or create generic ones
    const sections = [
      {
        name: "Technical Knowledge",
        score: Math.min(100, score + Math.floor(Math.random() * 10) - 5),
      },
      {
        name: "Problem Solving",
        score: Math.min(100, score + Math.floor(Math.random() * 10) - 5),
      },
      {
        name: "Application Skills",
        score: Math.min(100, score + Math.floor(Math.random() * 10) - 5),
      },
      {
        name: "Best Practices",
        score: Math.min(100, score + Math.floor(Math.random() * 10) - 5),
      },
    ];

    return sections.map((section) => ({
      ...section,
      score: Math.max(0, section.score), // Ensure no negative scores
    }));
  };

  const generateFeedback = (resultData) => {
    const score = resultData.score || 0;
    const correctAnswers = resultData.correctAnswers || 0;
    const totalQuestions = resultData.totalQuestions || 20;

    if (score >= 90) {
      return `Excellent performance! You scored ${score}% by correctly answering ${correctAnswers} out of ${totalQuestions} questions. You demonstrate strong mastery of the subject matter and are well-prepared for this role.`;
    } else if (score >= 80) {
      return `Good job! You scored ${score}% by correctly answering ${correctAnswers} out of ${totalQuestions} questions. You have a solid understanding of the core concepts with room for improvement in specific areas.`;
    } else if (score >= 70) {
      return `Decent performance. You scored ${score}% by correctly answering ${correctAnswers} out of ${totalQuestions} questions. You have a basic understanding but would benefit from additional study in key areas.`;
    } else {
      return `You scored ${score}% by correctly answering ${correctAnswers} out of ${totalQuestions} questions. Consider reviewing the fundamental concepts and practicing more before retaking the assessment.`;
    }
  };

  const generateStrengths = (resultData) => {
    const score = resultData.score || 0;

    const allStrengths = [
      "Technical Knowledge",
      "Problem Solving Approach",
      "Analytical Thinking",
      "Code Understanding",
      "Best Practices Awareness",
      "Quick Learning",
      "Attention to Detail",
      "Logical Reasoning",
    ];

    const numStrengths =
      score >= 90 ? 4 : score >= 80 ? 3 : score >= 70 ? 2 : 1;
    return allStrengths.slice(0, numStrengths);
  };

  const generateImprovements = (resultData) => {
    const score = resultData.score || 0;

    const allImprovements = [
      "Advanced Concepts",
      "Performance Optimization",
      "Error Handling",
      "Testing Strategies",
      "Design Patterns",
      "Security Best Practices",
      "Code Quality",
      "Documentation Skills",
    ];

    const numImprovements =
      score < 70 ? 4 : score < 80 ? 3 : score < 90 ? 2 : 1;
    return allImprovements.slice(0, numImprovements);
  };

  // Enhanced generators for interview results
  const generateInterviewSections = (resultData) => {
    const sections = [
      {
        name: "MCQ Test",
        score: Math.round(((resultData.mcqScore || 0) / 25) * 100), // Convert to percentage
        maxScore: 100,
      },
      {
        name: "AI Interview",
        score: Math.round(((resultData.interviewScore || 0) / 75) * 100), // Convert to percentage
        maxScore: 100,
      }
    ];

    // Add detailed breakdown if assessment data is available
    if (resultData.detailedScoring) {
      sections.push({
        name: "Technical Knowledge",
        score: Math.round(((resultData.detailedScoring.technicalKnowledge || 0) / 25) * 100),
        maxScore: 100,
        rawScore: resultData.detailedScoring.technicalKnowledge || 0,
        rawMaxScore: 25,
      });
      sections.push({
        name: "Problem Solving",
        score: Math.round(((resultData.detailedScoring.problemSolving || 0) / 25) * 100),
        maxScore: 100,
        rawScore: resultData.detailedScoring.problemSolving || 0,
        rawMaxScore: 25,
      });
      sections.push({
        name: "Communication",
        score: Math.round(((resultData.detailedScoring.communication || 0) / 15) * 100),
        maxScore: 100,
        rawScore: resultData.detailedScoring.communication || 0,
        rawMaxScore: 15,
      });
      sections.push({
        name: "Job Fit",
        score: Math.round(((resultData.detailedScoring.jobFit || 0) / 10) * 100),
        maxScore: 100,
        rawScore: resultData.detailedScoring.jobFit || 0,
        rawMaxScore: 10,
      });
    }

    return sections;
  };

  const generateInterviewFeedback = (resultData) => {
    const totalScore = resultData.totalScore || 0;
    const mcqScore = resultData.mcqScore || 0;
    const interviewScore = resultData.interviewScore || 0;
    const recommendationForHiring = resultData.recommendationForHiring;

    let feedback = `You completed the full assessment with a total score of ${totalScore.toFixed(1)}/100. `;
    feedback += `Your MCQ performance was ${mcqScore.toFixed(1)}/25 and your AI interview score was ${interviewScore.toFixed(1)}/75. `;

    if (totalScore >= 90) {
      feedback += "Outstanding performance! You demonstrated exceptional skills across all assessment areas.";
    } else if (totalScore >= 80) {
      feedback += "Excellent work! You showed strong competency in both technical knowledge and interview skills.";
    } else if (totalScore >= 70) {
      feedback += "Good performance overall with solid understanding demonstrated in the assessment.";
    } else if (totalScore >= 60) {
      feedback += "Decent performance with room for improvement in several key areas.";
    } else {
      feedback += "Consider focusing on strengthening your foundational knowledge and interview communication skills.";
    }

    if (recommendationForHiring) {
      feedback += ` Based on your performance, the AI interviewer's recommendation is: ${recommendationForHiring}.`;
    }

    if (resultData.keyFindings) {
      feedback += ` Key finding: ${resultData.keyFindings}`;
    }

    return feedback;
  };

  const generateInterviewStrengths = (resultData) => {
    const strengths = [];
    
    // Use AI assessment strengths if available
    if (resultData.jobFitAssessment) {
      try {
        const assessment = JSON.parse(resultData.jobFitAssessment);
        if (assessment.strengths && Array.isArray(assessment.strengths)) {
          return assessment.strengths;
        }
      } catch (error) {
        console.warn("Failed to parse job fit assessment for strengths");
      }
    }

    // Fallback based on scores
    const detailedScoring = resultData.detailedScoring || {};
    
    if (detailedScoring.technicalKnowledge > 20) strengths.push("Strong Technical Knowledge");
    if (detailedScoring.problemSolving > 20) strengths.push("Excellent Problem Solving");
    if (detailedScoring.communication > 12) strengths.push("Clear Communication");
    if (detailedScoring.jobFit > 8) strengths.push("Good Job Fit");
    if (resultData.mcqScore > 20) strengths.push("Strong MCQ Performance");
    
    return strengths.length > 0 ? strengths : ["Completed full assessment", "Interview participation"];
  };

  const generateInterviewImprovements = (resultData) => {
    const improvements = [];
    
    // Use AI assessment weaknesses if available
    if (resultData.jobFitAssessment) {
      try {
        const assessment = JSON.parse(resultData.jobFitAssessment);
        if (assessment.weaknesses && Array.isArray(assessment.weaknesses)) {
          return assessment.weaknesses;
        }
      } catch (error) {
        console.warn("Failed to parse job fit assessment for improvements");
      }
    }

    // Fallback based on scores
    const detailedScoring = resultData.detailedScoring || {};
    
    if (detailedScoring.technicalKnowledge < 15) improvements.push("Technical Knowledge Depth");
    if (detailedScoring.problemSolving < 15) improvements.push("Problem-Solving Approach");
    if (detailedScoring.communication < 10) improvements.push("Communication Clarity");
    if (detailedScoring.jobFit < 6) improvements.push("Role-Specific Experience");
    if (resultData.mcqScore < 15) improvements.push("Fundamental Concepts");
    
    return improvements.length > 0 ? improvements : ["Continue learning", "Practice interviewing"];
  };

  // Handler to check Pro status before generating course (HR tests only)
  const handleGenerateCourse = (testResultId, testResultData) => {
    // HR tests are stored in jobseekerResults collection
    // For HR tests, check if user is Pro
    if (!isPro) {
      // Show upgrade modal for free users
      setShowUpgradeModal(true);
      return;
    }

    // If Pro, generate the course
    generateCourse(testResultId, testResultData);
  };

  // Generate AI Course based on test mistakes
  const generateCourse = async (testResultId, testResultData) => {
    setGeneratingCourse(true);
    setCourseGenerationStatus("Analyzing your mistakes...");

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      const idToken = await currentUser.getIdToken();

      console.log(`Generating personalized course for test result: ${testResultId}`);

      // Fetch profile data if not already loaded
      let userProfileData = profileData;
      if (!userProfileData) {
        console.log("Fetching profile data for course generation...");
        const profileDoc = await getDoc(doc(db, "userProfiles", user?.id));
        userProfileData = profileDoc.data();
        setProfileData(userProfileData);
      }

      // Prepare test data for analysis
      // For HR tests with interview: combine MCQ questions and interview mistakes
      const testQuestions = testResultData.testData?.questions || testResultData.questions || [];
      const userAnswers = testResultData.answers || {};

      // Convert answers object to array of indices
      const userAnswerIndices = [];
      const correctAnswerIndices = [];

      testQuestions.forEach((question, index) => {
        const userAnswer = userAnswers[index];
        const correctAnswer = question.correctAnswer;

        // Find indices
        const userAnswerIndex = question.options?.indexOf(userAnswer) ?? -1;
        const correctAnswerIndex = question.options?.indexOf(correctAnswer) ?? 0;

        userAnswerIndices.push(userAnswerIndex);
        correctAnswerIndices.push(correctAnswerIndex);
      });

      // Call analyzeTestMistakes function
      console.log("Calling analyzeTestMistakes function...");
      const mistakesResponse = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/analyzeTestMistakes",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            testQuestions: testQuestions,
            userAnswers: userAnswerIndices,
            correctAnswers: correctAnswerIndices,
            resumeData: userProfileData?.resumeData,
            // Include interview data if available
            interviewData: testResultData.hasInterviewData ? {
              assessmentData: testResultData.assessmentData,
              improvements: testResultData.improvements,
              keyFindings: testResultData.keyFindings,
            } : null,
          }),
        }
      );

      if (!mistakesResponse.ok) {
        const errorText = await mistakesResponse.text();
        console.error("analyzeTestMistakes failed:", errorText);

        // Provide user-friendly error messages
        if (mistakesResponse.status === 401 || mistakesResponse.status === 403) {
          throw new Error("Authentication failed. Please try logging in again.");
        } else if (mistakesResponse.status === 500) {
          throw new Error("Server error. Our AI service is temporarily unavailable. Please try again in a few minutes.");
        } else if (mistakesResponse.status === 404) {
          throw new Error("Course generation service not found. Please contact support.");
        } else {
          throw new Error(`Failed to analyze mistakes. Please try again or contact support if the issue persists.`);
        }
      }

      const mistakesAnalysis = await mistakesResponse.json();
      console.log("Mistakes analyzed successfully:", mistakesAnalysis);

      // Store mistakes analysis
      console.log("Storing mistakes analysis in Firestore...");

      // Build the document data, filtering out undefined values
      const mistakesDocData = {
        userId: user?.id,
        testResultId: testResultId,
        createdAt: Timestamp.now(),
      };

      // Only add fields that are defined (not undefined)
      if (mistakesAnalysis.mistakes !== undefined) {
        mistakesDocData.mistakes = mistakesAnalysis.mistakes;
      }
      if (mistakesAnalysis.weakAreas !== undefined) {
        mistakesDocData.weakAreas = mistakesAnalysis.weakAreas;
      }
      if (mistakesAnalysis.mistakePatterns !== undefined) {
        mistakesDocData.mistakePatterns = mistakesAnalysis.mistakePatterns;
      }
      if (mistakesAnalysis.recommendedFocus !== undefined) {
        mistakesDocData.recommendedFocus = mistakesAnalysis.recommendedFocus;
      }
      if (mistakesAnalysis.detailedAnalysis !== undefined) {
        mistakesDocData.detailedAnalysis = mistakesAnalysis.detailedAnalysis;
      }

      await setDoc(doc(db, "testMistakes", testResultId), mistakesDocData);
      console.log("Mistakes analysis stored successfully");
      setMistakesData(mistakesAnalysis);

      // Generate personalized course
      setCourseGenerationStatus("Creating your personalized learning course...");
      console.log("Calling generatePersonalizedCourse function...");

      const courseResponse = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generatePersonalizedCourse",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            mistakes: mistakesAnalysis.mistakes,
            weakAreas: mistakesAnalysis.weakAreas,
            recommendedFocus: mistakesAnalysis.recommendedFocus,
            resumeData: userProfileData?.resumeData,
          }),
        }
      );

      if (!courseResponse.ok) {
        const errorText = await courseResponse.text();
        console.error("generatePersonalizedCourse failed:", errorText);
        throw new Error(`Failed to generate course: ${errorText}`);
      }

      const courseData = await courseResponse.json();
      console.log("Personalized course generated successfully");

      // Store personalized course
      console.log("Storing personalized course in Firestore...");
      const courseRef = await addDoc(collection(db, "personalizedCourses"), {
        userId: user?.id,
        testResultId: `hr:${testResultId}`,
        testType: "hr_test",
        courseData: courseData.course,
        basedOnMistakes: mistakesAnalysis.weakAreas,
        weakAreasLabels: mistakesAnalysis.weakAreas,
        completed: false,
        courseTestPassed: false,
        createdAt: Timestamp.now(),
      });
      console.log("Personalized course stored successfully with ID:", courseRef.id);

      setCourseId(courseRef.id);
      setGeneratingCourse(false);
      toast.success("🎉 Your personalized course is ready!");
    } catch (error) {
      console.error("Error generating course:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("Full error details:", errorMessage);
      toast.error(`Course generation failed: ${errorMessage}`);
      setGeneratingCourse(false);
    }
  };

  // Check if course already exists for this test result
  const checkExistingCourse = async (testResultId) => {
    try {
      const prefixedId = `hr:${testResultId}`;
      const coursesQuery = query(
        collection(db, "personalizedCourses"),
        where("testResultId", "in", [prefixedId, testResultId]),
        limit(1)
      );
      const coursesSnapshot = await getDocs(coursesQuery);
      if (!coursesSnapshot.empty) {
        const existingCourse = coursesSnapshot.docs[0];
        setCourseId(existingCourse.id);

        // Also check for mistakes data
        const mistakesDoc = await getDoc(doc(db, "testMistakes", testResultId));
        if (mistakesDoc.exists()) {
          setMistakesData(mistakesDoc.data());
        }

        return existingCourse.id;
      }
      return null;
    } catch (error) {
      console.error("Error checking for existing course:", error);
      return null;
    }
  };

  // Fetch real test results from Firebase
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "jobseekerResults"),
      where("jobSeekerId", "==", user.id),
      orderBy("completedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const results = [];
      for (const docSnap of snapshot.docs) {
        const resultData = docSnap.data();

        // Fetch the original test data to get more details
        let testData = null;
        if (resultData.testId) {
          try {
            // Try hrTests first
            let testDoc = await getDoc(doc(db, "hrTests", resultData.testId));
            if (!testDoc.exists()) {
              // If not found, try skillsValidationTests
              testDoc = await getDoc(
                doc(db, "skillsValidationTests", resultData.testId)
              );
            }
            if (testDoc.exists()) {
              testData = testDoc.data();
            }
          } catch (error) {
            console.error("Error fetching test data:", error);
          }
        }

        // Determine if this is an AI interview result or just MCQ
        const hasInterviewData = resultData.hasCompletedInterview && 
                                typeof resultData.interviewScore === 'number';
        const testType = hasInterviewData ? "Full Assessment (MCQ + AI Interview)" : "Technical Test";
        const finalScore = hasInterviewData ? resultData.totalScore : resultData.score;

        results.push({
          id: docSnap.id,
          title: resultData.jobTitle || testData?.jobTitle || "Test",
          company: testData?.hrEmail || "Company",
          type: testType,
          status: "completed",
          score: finalScore || 0,
          maxScore: 100,
          completedDate:
            (resultData.completedAt
              ?.toDate?.()
              ?.toISOString?.()
              ?.split("T")?.[0]) || new Date().toISOString().split("T")[0],
          duration: testData?.duration ? `${testData.duration} minutes` : "N/A",
          totalQuestions: resultData.totalQuestions || 0,
          correctAnswers: resultData.correctAnswers || 0,
          
          // Enhanced badge calculation based on total score
          badge:
            finalScore >= 90
              ? "gold"
              : finalScore >= 75
              ? "silver"
              : finalScore >= 60
              ? "bronze"
              : "participant",
              
          testData: testData,
          answers: resultData.answers || {},
          
          // AI Interview specific data
          hasInterviewData,
          mcqScore: resultData.mcqScore || 0,
          interviewScore: resultData.interviewScore || 0,
          totalScore: resultData.totalScore || resultData.score || 0,
          assessmentData: resultData.jobFitAssessment ? 
            (() => {
              try {
                return JSON.parse(resultData.jobFitAssessment);
              } catch {
                return null;
              }
            })() : null,
          recommendationForHiring: resultData.recommendationForHiring || null,
          keyFindings: resultData.keyFindings || null,

          // Generate sections based on whether interview w
          sections: hasInterviewData ? 
            generateInterviewSections(resultData) : generateSections(resultData),
          feedback: hasInterviewData ? 
            generateInterviewFeedback(resultData) : generateFeedback(resultData),
          strengths: hasInterviewData ? 
            generateInterviewStrengths(resultData) : generateStrengths(resultData),
          improvements: hasInterviewData ? 
            generateInterviewImprovements(resultData) : generateImprovements(resultData),
        });
      }
      setTestResults(results);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, db]);

  // Check for existing course when a test is selected
  useEffect(() => {
    if (selectedTest) {
      // Reset course state
      setCourseId(null);
      setMistakesData(null);

      // Check if course already exists for this test
      checkExistingCourse(selectedTest);
    }
  }, [selectedTest]);

  // Mock data for fallback (remove this later if needed)
  const mockTestResults = [
    {
      id: "1",
      title: "React Developer Assessment",
      company: "TechCorp Inc.",
      type: "Technical Test",
      status: "completed",
      score: 88,
      maxScore: 100,
      completedDate: "2025-01-10",
      duration: "45 minutes",
      badge: "gold",
      sections: [
        { name: "React Fundamentals", score: 92, maxScore: 100 },
        { name: "State Management", score: 85, maxScore: 100 },
        { name: "Component Design", score: 90, maxScore: 100 },
        { name: "Performance Optimization", score: 82, maxScore: 100 },
      ],
      feedback:
        "Excellent understanding of React concepts. Strong performance in component design and fundamentals. Consider reviewing performance optimization techniques.",
      strengths: ["Component Architecture", "React Hooks", "JSX Proficiency"],
      improvements: ["Performance Optimization", "Testing Strategies"],
    },
    {
      id: "2",
      title: "AI Interview - Full Stack Role",
      company: "StartupXYZ",
      type: "AI Interview",
      status: "completed",
      score: 92,
      maxScore: 100,
      completedDate: "2025-01-12",
      duration: "30 minutes",
      badge: "gold",
      sections: [
        { name: "Technical Knowledge", score: 90, maxScore: 100 },
        { name: "Problem Solving", score: 95, maxScore: 100 },
        { name: "Communication", score: 88, maxScore: 100 },
        { name: "Cultural Fit", score: 95, maxScore: 100 },
      ],
      feedback:
        "Outstanding interview performance. Demonstrated strong technical knowledge and excellent communication skills. Great cultural fit for our team.",
      strengths: [
        "Problem Solving",
        "Technical Communication",
        "Team Collaboration",
      ],
      improvements: ["System Design", "Leadership Experience"],
    },
    {
      id: "3",
      title: "Scenario-Based Assessment",
      company: "DataTech Solutions",
      type: "Scenario Test",
      status: "completed",
      score: 78,
      maxScore: 100,
      completedDate: "2025-01-08",
      duration: "60 minutes",
      badge: "silver",
      sections: [
        { name: "Problem Analysis", score: 85, maxScore: 100 },
        { name: "Solution Design", score: 75, maxScore: 100 },
        { name: "Implementation Strategy", score: 72, maxScore: 100 },
        { name: "Risk Assessment", score: 80, maxScore: 100 },
      ],
      feedback:
        "Good analytical skills and problem-solving approach. Solutions are practical but could benefit from more detailed implementation strategies.",
      strengths: ["Problem Analysis", "Risk Assessment", "Practical Solutions"],
      improvements: ["Implementation Details", "Scalability Considerations"],
    },
    {
      id: "4",
      title: "Python Backend Test",
      company: "CloudTech Inc.",
      type: "Technical Test",
      status: "in_review",
      completedDate: "2025-01-14",
      duration: "90 minutes",
    },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case "in_review":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            In Review
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  const getBadgeIcon = (badge) => {
    if (!badge) return null;

    return (
      <div
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          badge === "gold"
            ? "bg-yellow-100 text-yellow-800"
            : "bg-gray-100 text-gray-800"
        }`}
      >
        <Trophy className="w-4 h-4 mr-1" />
        {badge.charAt(0).toUpperCase() + badge.slice(1)} Badge
      </div>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600";
    return "text-red-700"; // light maroon color for scores below 90%
  };

  const selectedTestData = testResults.find((test) => test.id === selectedTest);

  // Handler for sharing test results
  const handleShare = async (testData) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Test Results: ${testData.title}`,
          text: `I scored ${testData.score}% on the ${testData.title} assessment at ${testData.company}!`,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Error sharing:", error);
        // Fallback to copying to clipboard
        fallbackShare(testData);
      }
    } else {
      // Fallback for browsers that don't support navigator.share
      fallbackShare(testData);
    }
  };

  const fallbackShare = (testData) => {
    const shareText = `I scored ${testData.score}% on the ${testData.title} assessment at ${testData.company}! 🎉`;
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(shareText)
        .then(() => {
          alert("Results copied to clipboard!");
        })
        .catch(() => {
          alert(
            "Unable to copy to clipboard. Please copy manually:\n\n" + shareText
          );
        });
    } else {
      alert("Share text:\n\n" + shareText);
    }
  };

  // Handler for downloading PDF
  const handleDownloadPDF = (testData) => {
    // Create a simple HTML content for PDF generation
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Test Results - ${testData.title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #ddd; padding-bottom: 20px; }
          .score { font-size: 48px; color: ${
            testData.score >= 90
              ? "#059669"
              : testData.score >= 80
              ? "#2563eb"
              : testData.score >= 70
              ? "#d97706"
              : "#dc2626"
          }; font-weight: bold; }
          .section { margin: 30px 0; }
          .section-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; color: #374151; }
          .section-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
          .section-item { background: #f9fafb; padding: 15px; border-radius: 8px; }
          .list-item { margin: 8px 0; padding-left: 20px; }
          .strength { color: #059669; }
          .improvement { color: #2563eb; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${testData.title}</h1>
          <p>${testData.company} • ${testData.type}</p>
          <p>Completed on ${testData.completedDate} • Duration: ${
      testData.duration
    }</p>
          <div class="score">${testData.score}%</div>
          <p>Overall Score</p>
        </div>
        
        ${
          testData.sections
            ? `
        <div class="section">
          <div class="section-title">Score Breakdown</div>
          <div class="section-grid">
            ${testData.sections
              .map(
                (section) => `
              <div class="section-item">
                <h3>${section.name}</h3>
                <div style="font-size: 24px; font-weight: bold; color: ${
                  section.score >= 90
                    ? "#059669"
                    : section.score >= 80
                    ? "#2563eb"
                    : section.score >= 70
                    ? "#d97706"
                    : "#dc2626"
                };">${section.score}%</div>
              </div>
            `
              )
              .join("")}
          </div>
        </div>
        `
            : ""
        }
        
        ${
          testData.feedback
            ? `
        <div class="section">
          <div class="section-title">Detailed Feedback</div>
          <p>${testData.feedback}</p>
        </div>
        `
            : ""
        }
        
        <div class="section-grid">
          ${
            testData.strengths
              ? `
          <div>
            <div class="section-title strength">Strengths</div>
            ${testData.strengths
              .map(
                (strength) =>
                  `<div class="list-item strength">• ${strength}</div>`
              )
              .join("")}
          </div>
          `
              : ""
          }
          
          ${
            testData.improvements
              ? `
          <div>
            <div class="section-title improvement">Are Improvement</div>
            ${testData.improvements
              .map(
                (improvement) =>
                  `<div class="list-item improvement">• ${improvement}</div>`
              )
              .join("")}
          </div>
          `
              : ""
          }
        </div>
        
        <div style="margin-top: 40px; text-align: center; color: #6b7280; font-size: 12px;">
          Generated by VetriaAI • ${new Date().toLocaleDateString()}
        </div>
      </body>
      </html>
    `;

    // Create a blob and download
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `test-results-${testData.title
      .replace(/[^a-z0-9]/gi, "_")
      .toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Note: For true PDF generation, you would typically use a library like jsPDF or html2pdf
    alert(
      "Test results downloaded  file. You can print this  from your browser."
    );
  };

  if (selectedTest && selectedTestData) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Tooltip content="Return to test results list">
            <button
              onClick={() => setSelectedTest(null)}
              className="text-red-600 hover:text-red-800 font-medium relative"
            >
              ← Back to Results
            </button>
          </Tooltip>
          <div className="flex space-x-3">
            <Tooltip content="Share your test results">
              <button
                onClick={() => handleShare(selectedTestData)}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors relative"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </button>
            </Tooltip>
            <Tooltip content="Download test results ">
              <button
                onClick={() => handleDownloadPDF(selectedTestData)}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors relative"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Test Overview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedTestData.title}
              </h1>
              <p className="text-gray-600 mt-1">
                {selectedTestData.company} • {selectedTestData.type}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Completed on {selectedTestData.completedDate} • Duration
                {selectedTestData.duration}
              </p>
            </div>
            <div className="text-right">
              <div
                className={`text-4xl font-bold ${getScoreColor(
                  selectedTestData.score || 0
                )}`}
              >
                {selectedTestData.score}%
              </div>
              <div className="text-gray-500 text-sm">Overall Score</div>
              {selectedTestData.badge && (
                <div className="mt-2">
                  {getBadgeIcon(selectedTestData.badge)}
                </div>
              )}
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {selectedTestData.sections?.map((section, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">
                  {section.name}
                </h3>
                <div className="flex items-center justify-between">
                  <span
                    className={`text-2xl font-bold ${getScoreColor(
                      section.score
                    )}`}
                  >
                    {section.score}%
                  </span>
                  <div className="w-16 h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-2 rounded-full ${
                        section.score >= 90
                          ? "bg-green-500"
                          : section.score >= 80
                          ? "bg-blue-500"
                          : section.score >= 70
                          ? "bg-yellow-500"
                          : "bg-red-500"
                      }`}
                      style={{ width: `${section.score}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Assessment Details for AI Interview Results */}
          {selectedTestData.hasInterviewData && selectedTestData.assessmentData && (
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                AI Interview Assessment
              </h3>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">MCQ Score:</span>
                    <span className="ml-2 text-blue-600 font-semibold">
                      {selectedTestData.mcqScore?.toFixed(1)}/25 ({Math.round((selectedTestData.mcqScore / 25) * 100)}%)
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Interview Score:</span>
                    <span className="ml-2 text-green-600 font-semibold">
                      {selectedTestData.interviewScore?.toFixed(1)}/75 ({Math.round((selectedTestData.interviewScore / 75) * 100)}%)
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Recommendation:</span>
                    <span className={`ml-2 font-semibold ${
                      selectedTestData.recommendationForHiring === 'STRONG RECOMMEND' || 
                      selectedTestData.recommendationForHiring === 'RECOMMEND' 
                        ? 'text-green-600' 
                        : selectedTestData.recommendationForHiring === 'NOT RECOMMEND' || 
                          selectedTestData.recommendationForHiring === 'STRONG REJECT'
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}>
                      {selectedTestData.recommendationForHiring}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Score:</span>
                    <span className="ml-2 text-gray-900 font-bold">
                      {selectedTestData.totalScore?.toFixed(1)}/100
                    </span>
                  </div>
                </div>
              </div>
              {selectedTestData.keyFindings && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Key Findings:</h4>
                  <p className="text-gray-700 text-sm">{selectedTestData.keyFindings}</p>
                </div>
              )}
            </div>
          )}

          {/* Feedback */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold text-gray-900 mb-3">
              Detailed Feedback
            </h3>
            <p className="text-gray-700 mb-6">{selectedTestData.feedback}</p>

            {/* Show AI Assessment narrative if available */}
            {selectedTestData.assessmentData?.narrativeAssessment && (
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-blue-900 mb-2">AI Interview Assessment:</h4>
                <p className="text-blue-800 text-sm">{selectedTestData.assessmentData.narrativeAssessment}</p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-green-900 mb-3 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Strengths
                </h4>
                <ul className="space-y-2">
                  {selectedTestData.strengths?.map((strength, index) => (
                    <li
                      key={index}
                      className="flex items-center text-green-700"
                    >
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                  <Target className="w-4 h-4 mr-2" />
                  Are Improvement
                </h4>
                <ul className="space-y-2">
                  {selectedTestData.improvements?.map((improvement, index) => (
                    <li key={index} className="flex items-center text-blue-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Performance Breakdown
          </h3>
          <div className="space-y-4">
            {selectedTestData.sections?.map((section, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {section.name}
                  </span>
                  <span className="text-sm text-gray-600">
                    {section.score}%
                    {section.rawScore !== undefined && section.rawMaxScore !== undefined && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({section.rawScore.toFixed(1)}/{section.rawMaxScore})
                      </span>
                    )}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      section.score >= 90
                        ? "bg-green-500"
                        : section.score >= 80
                        ? "bg-blue-500"
                        : section.score >= 70
                        ? "bg-yellow-500"
                        : "bg-red-500"
                    }`}
                    style={{ width: `${section.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Course Generation Section */}
        {selectedTestData.score < 90 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            {generatingCourse ? (
              // Show loading state while generating course
              <div className="text-center py-8">
                <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {courseGenerationStatus}
                </h2>
                <p className="text-gray-600">
                  Please wait while we create your personalized learning course...
                </p>
              </div>
            ) : courseId ? (
              // Show course ready message
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Sparkles className="w-6 h-6" />
                      <h3 className="text-xl font-bold">
                        Your Personalized Learning Course is Ready!
                      </h3>
                    </div>
                    <p className="text-blue-100 mb-4">
                      We've created a custom course just for you based on your
                      test performance. Learn the concepts you struggled with and
                      improve your skills!
                    </p>
                    {mistakesData && (
                      <div className="bg-white/10 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold mb-2">Course Covers:</h4>
                        <div className="flex flex-wrap gap-2">
                          {mistakesData.weakAreas?.map((area, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-white/20 rounded-full text-sm"
                            >
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        if (!isPro) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        navigate(`/job-seeker/personalized-course/${courseId}`);
                      }}
                      className="flex items-center space-x-2 bg-white text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
                    >
                      <BookOpen className="w-5 h-5" />
                      <span>View Full Course</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Show generate course button
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    AI-Powered Learning Course
                  </h2>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-blue-900 mb-3">
                    Want to improve your skills?
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Our AI can analyze your test performance and create a personalized
                    learning course tailored to help you master the are you need
                    improvement. The course includes:
                  </p>
                  <ul className="space-y-2 text-blue-800 mb-6">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-blue-600" />
                      <span>Detailed explanations of concepts you struggled with</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-blue-600" />
                      <span>Interactive lessons and practical examples</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-blue-600" />
                      <span>Course completion test to validate your learning</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 mt-0.5 text-blue-600" />
                      <span>Personalized based on your mistakes and skill gaps</span>
                    </li>
                  </ul>

                  <button
                    onClick={() => handleGenerateCourse(selectedTestData.id, selectedTestData)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                  >
                    <Sparkles className="w-5 h-5" />
                    <span>Generate AI Course</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Perfect Score Message */}
        {selectedTestData.score >= 90 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Outstanding Performance!
              </h2>
              <p className="text-gray-600">
                You scored {selectedTestData.score}% - Excellent work! You've demonstrated
                strong mastery of the subject matter. No AI course needed for this result.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <PremiumPaywall isPremium={isPro}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your test results...</p>
        </div>
      </div>
      </PremiumPaywall>
    );
  }

  if (!testResults.length) {
    return (
      <PremiumPaywall isPremium={isPro}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-600 mb-2">
            No Test Results Yet
          </h2>
          <p className="text-gray-500 mb-6">
            You haven't completed any tests yet. Take your first test to see
            results here!
          </p>
          <Link
            to="/job-seeker/available-tests"
            className="inline-flex items-center px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Browse Available Tests
          </Link>
        </div>
      </div>
      </PremiumPaywall>
    );
  }

  return (
    <PremiumPaywall isPremium={isPro}>
    <div className="space-y-6">
      {/* Verification Prompt Modal for HR-shared test completion */}
      {showVerificationPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Great Job on Your Assessment!
            </h2>
            <p className="text-gray-600 mb-6">
              You've successfully completed your first job assessment! To unlock more opportunities and apply for other positions on our platform, complete your skills verification.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowVerificationPrompt(false);
                  navigate("/job-seeker/skills-validation");
                }}
                className="w-full px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors font-medium inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                Get Verified Now
              </button>
              <button
                onClick={() => setShowVerificationPrompt(false)}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Maybe Later
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Verified candidates get better visibility to recruiters
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Test Results</h1>
          <p className="text-gray-600 mt-1">
            View your assessment results and performance analytics
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {testResults.length}
          </div>
          <div className="text-sm text-gray-600">Tests Completed</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className={`text-2xl font-bold ${
            testResults.length > 0
              ? (() => {
                  const avgScore = Math.round(
                    testResults.reduce((sum, test) => sum + test.score, 0) /
                      testResults.length
                  );
                  return avgScore >= 90 ? "text-green-600" : "text-red-700";
                })()
              : "text-gray-400"
          }`}>
            {testResults.length > 0
              ? Math.round(
                  testResults.reduce((sum, test) => sum + test.score, 0) /
                    testResults.length
                )
              : 0}
            %
          </div>
          <div className="text-sm text-gray-600">Average Score</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-2xl font-bold text-red-600">
            {testResults.filter(test => test.hasInterviewData).length}
          </div>
          <div className="text-sm text-gray-600">AI Interviews</div>
        </div>
      </div>

      {/* Test Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Recent Test Results
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {testResults.map((test) => (
            <div
              key={test.id}
              className="p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {test.title}
                    </h3>
                    {getStatusBadge(test.status)}
                    {test.badge && getBadgeIcon(test.badge)}
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>{test.company}</span>
                    <span>•</span>
                    <span>{test.type}</span>
                    <span>•</span>
                    <span>{test.completedDate}</span>
                    <span>•</span>
                    <span>{test.duration}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  {test.score && (
                    <div className="text-right">
                      <div
                        className={`text-2xl font-bold ${getScoreColor(
                          test.score
                        )}`}
                      >
                        {test.score}%
                      </div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    {test.status === "completed" && (
                      <Tooltip content="View detailed test results">
                        <button
                          onClick={() => setSelectedTest(test.id)}
                          className="flex items-center px-3 py-2 bg-red-800 text-white text-sm rounded-lg hover:bg-red-900 transition-colors relative"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </button>
                      </Tooltip>
                    )}
                    {test.status === "in_review" && (
                      <span className="px-3 py-2 bg-yellow-100 text-yellow-800 text-sm rounded-lg">
                        Pending Review
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {test.sections && test.status === "completed" && (
                <div className="mt-4">
                  {/* Special layout for AI interview results */}
                  {test.hasInterviewData ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {test.sections.slice(0, 6).map((section, index) => (
                        <div key={index} className="text-center">
                          <div
                            className={`text-lg font-semibold ${getScoreColor(
                              section.score
                            )}`}
                          >
                            {section.score}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {section.name}
                          </div>
                        </div>
                      ))}
                      {/* Show actual scores for AI interview */}
                      <div className="col-span-2 md:col-span-3 mt-2 pt-2 border-t border-gray-200">
                        <div className="flex justify-center space-x-6 text-sm">
                          <div className="text-center">
                            <div className="text-blue-600 font-medium">
                              {test.mcqScore?.toFixed(1)}/25
                            </div>
                            <div className="text-xs text-gray-500">MCQ</div>
                          </div>
                          <div className="text-center">
                            <div className="text-green-600 font-medium">
                              {test.interviewScore?.toFixed(1)}/75
                            </div>
                            <div className="text-xs text-gray-500">Interview</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-900 font-bold">
                              {test.totalScore?.toFixed(1)}/100
                            </div>
                            <div className="text-xs text-gray-500">Total</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {test.sections.slice(0, 4).map((section, index) => (
                        <div key={index} className="text-center">
                          <div
                            className={`text-lg font-semibold ${getScoreColor(
                              section.score
                            )}`}
                          >
                            {section.score}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {section.name}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="AI Course Generation for HR Tests"
      />
    </div>
    </PremiumPaywall>
  );
};

export default TestResults;
